import React, { useState } from 'react';
import { PhoneCall, ChevronDown, Star, CheckCircle2, ArrowRight, Quote, Sparkles } from 'lucide-react';
import PageLayout, { C, serif } from './PageLayout';
import { Button } from './ui/button';
import { siteData } from '../data/siteData';

const d = siteData;
const gradBtn = { background: `linear-gradient(135deg, ${C.accent}, #d42d95)`, boxShadow: `0 12px 40px ${C.accent}50` };

const Eyebrow = ({ children, light }) => (
  <div className="inline-flex items-center gap-2 mb-5 px-4 py-1.5 rounded-full" style={{ background: `${C.accent}${light ? '10' : '15'}`, border: `1px solid ${C.accent}30` }}>
    <Sparkles className="w-3.5 h-3.5" style={{ color: C.accent }} />
    <span className="text-[11px] font-semibold tracking-[0.2em] uppercase" style={{ color: C.accent }}>{children}</span>
  </div>
);

const BookButton = ({ onClick, label, testId, size = 'lg' }) => (
  <Button size="lg" className={`group relative text-white font-bold rounded-full shadow-2xl hover:scale-105 transition-transform duration-300 overflow-hidden ${size === 'xl' ? 'h-16 px-12 text-lg' : 'h-14 px-10 text-base'}`} style={gradBtn} onClick={onClick} data-testid={testId}>
    <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
    <PhoneCall className="w-5 h-5 mr-2 relative z-10" /><span className="relative z-10">{label}</span>
  </Button>
);

const Hero = ({ hero, onBook }) => (
  <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="page-hero">
    <div className="absolute inset-0">
      <video autoPlay loop muted playsInline preload="auto" poster={hero.poster} className="w-full h-full object-cover opacity-50">
        <source src="/videos/hero-bg.webm" type="video/webm" />
        <source src="/videos/hero-bg.mp4" type="video/mp4" />
      </video>
      <div className="absolute inset-0" style={{ background: `linear-gradient(to right, ${C.bg}88 0%, ${C.bg}55 50%, ${C.bg}88 100%)` }} />
      <div className="absolute inset-x-0 bottom-0 h-40" style={{ background: `linear-gradient(to top, ${C.bg}, transparent)` }} />
    </div>
    <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 pb-20 text-center">
      <div className="inline-flex items-center gap-2 mb-8 px-4 py-2 rounded-full" style={{ background: `${C.accent}15`, border: `1px solid ${C.accent}30` }}>
        <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: C.accent }} />
        <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: C.accent }}>{hero.badge}</span>
      </div>
      <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-[1.08]" style={serif} data-testid="page-h1">
        {hero.h1}<br /><span style={{ color: C.accent }}>{hero.h1Accent}</span>
      </h1>
      <div className="inline-flex items-center gap-3 mb-8 px-5 py-3 rounded-xl" style={{ background: `linear-gradient(135deg, ${C.accent}20, ${C.accent}08)`, border: `2px solid ${C.accent}50` }} data-testid="hero-highlight">
        <Star className="w-6 h-6 fill-amber-400 text-amber-400 flex-shrink-0" />
        <span className="text-white font-bold text-base sm:text-lg">{hero.highlight}</span>
      </div>
      <p className="text-lg text-gray-300 mb-10 leading-relaxed max-w-2xl mx-auto">{hero.intro}</p>
      <div className="flex flex-wrap gap-3 justify-center mb-10">
        {[['10+', 'Years'], ['1000+', 'Events'], ['5.0★', 'Google']].map(([n, l]) => (
          <div key={n} className="px-5 py-3 rounded-xl backdrop-blur-sm" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="text-xl font-bold text-white">{n}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-[0.15em]">{l}</div>
          </div>
        ))}
      </div>
      <BookButton onClick={onBook} label="Book Your Show" testId="hero-enquire-btn" size="xl" />
    </div>
  </section>
);

const Highlights = ({ items }) => (
  <section className="py-20 relative overflow-hidden" style={{ background: C.bg }} data-testid="highlights-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {items.map((h, i) => (
        <div key={h.title} className="group relative p-7 rounded-2xl overflow-hidden transition-transform duration-300 hover:-translate-y-1.5" style={{ background: 'linear-gradient(160deg, rgba(255,255,255,0.05), rgba(255,255,255,0.01))', border: '1px solid rgba(255,255,255,0.07)' }} data-testid={`highlight-card-${i}`}>
          <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-3xl opacity-0 group-hover:opacity-60 transition-opacity duration-500" style={{ background: C.accent }} />
          <div className="relative w-14 h-14 rounded-xl flex items-center justify-center mb-5" style={{ background: `${C.accent}18`, color: C.accent }}>
            <h.icon className="w-7 h-7" />
          </div>
          <h3 className="relative text-lg font-bold text-white mb-2">{h.title}</h3>
          <p className="relative text-sm text-gray-400 leading-relaxed">{h.text}</p>
        </div>
      ))}
    </div>
  </section>
);

const IntroSplit = ({ intro, image, imageAlt, onBook }) => (
  <section className="py-24 bg-white overflow-hidden" data-testid="page-content">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
      <div>
        <Eyebrow light>{intro.eyebrow}</Eyebrow>
        <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight" style={serif}>{intro.title}</h2>
        <div className="w-16 h-0.5 mb-8" style={{ background: C.accent }} />
        <p className="text-lg text-gray-800 leading-relaxed mb-5 font-medium">{intro.p[0]}</p>
        {intro.p.slice(1).map(t => <p key={t} className="text-gray-600 text-[15px] leading-[1.85] mb-4">{t}</p>)}
        <button onClick={onBook} className="mt-4 inline-flex items-center gap-2 font-semibold text-sm group" style={{ color: C.accent }} data-testid="intro-enquire-link">
          Check availability for your date <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
      <div className="relative">
        <div className="absolute -inset-2 sm:-inset-4 rounded-[2rem] rotate-2 sm:rotate-3" style={{ background: `linear-gradient(135deg, ${C.accent}30, transparent)` }} />
        <img src={image} alt={imageAlt} className="relative w-full aspect-[4/5] object-cover object-top rounded-[1.75rem] shadow-2xl" />
        <div className="absolute -bottom-6 left-3 sm:-left-8 bg-white rounded-2xl shadow-xl p-5 flex items-center gap-4 border border-gray-100">
          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: `${C.accent}15` }}>
            <Star className="w-6 h-6 fill-amber-400 text-amber-400" />
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900" style={serif}>5.0 Google</div>
            <div className="text-xs text-gray-500">Melbourne's most reviewed agency</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const CardSections = ({ sections }) => (
  <section className="py-24 bg-[#f7f7f9]" data-testid="content-cards">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
      {sections.map((s, i) => (
        <div key={s.h2} className={`relative bg-white rounded-2xl p-8 lg:p-10 shadow-sm border border-gray-100 hover:shadow-xl transition-shadow duration-300 ${sections.length % 2 && i === sections.length - 1 ? 'md:col-span-2' : ''}`}>
          <span className="absolute top-6 right-8 text-6xl font-bold opacity-10" style={{ ...serif, color: C.accent }}>{String(i + 1).padStart(2, '0')}</span>
          <div className="w-10 h-1 rounded-full mb-6" style={{ background: C.accent }} />
          <h2 className="text-2xl font-bold text-gray-900 mb-4 pr-12" style={serif}>{s.h2}</h2>
          {s.p.map(t => <p key={t} className="text-gray-600 text-[15px] leading-[1.85] mb-3">{t}</p>)}
        </div>
      ))}
    </div>
  </section>
);

const ModelsModule = ({ subtitle, offset, onBook }) => {
  const [showAll, setShowAll] = useState(false);
  const ordered = [...d.gallery.slice(offset), ...d.gallery.slice(0, offset)];
  const list = showAll ? ordered : ordered.slice(0, 8);
  return (
    <section className="py-24 relative overflow-hidden" style={{ background: C.bg }} data-testid="models-section">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full blur-[140px] opacity-20" style={{ background: C.accent }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <Eyebrow>{d.gallery.length} Models Available</Eyebrow>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4" style={serif}>Meet Our <span style={{ color: C.accent }}>Girls</span></h2>
          <p className="text-gray-400 max-w-xl mx-auto">{subtitle}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {list.map((g, i) => (
            <div key={g.photo} className="group relative rounded-2xl overflow-hidden aspect-[3/4] cursor-pointer border border-white/5 hover:border-[#FD3DB5] hover:shadow-[0_0_40px_rgba(253,61,181,0.35)] transition-[border-color,box-shadow] duration-300" onClick={onBook} data-testid={`model-card-${i}`}>
              <img src={g.photo.replace('./', '/')} alt={`${g.name} - Melbourne entertainer`} decoding="async" className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-4 sm:p-5 flex items-end justify-between">
                <span className="text-white font-bold text-lg sm:text-xl" style={serif}>{g.name}</span>
                <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-white px-3 py-1.5 rounded-full opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300" style={{ background: C.accent }}>Book</span>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-12">
          <Button variant="outline" className="rounded-full h-12 px-8 text-sm font-semibold bg-transparent hover:bg-white/5" style={{ borderColor: `${C.accent}70`, color: C.accent }} onClick={() => setShowAll(!showAll)} data-testid="models-toggle-btn">
            {showAll ? 'Show fewer models' : `View all ${d.gallery.length} models`}
          </Button>
        </div>
      </div>
    </section>
  );
};

const EditorialSections = ({ sections, image, imageAlt }) => (
  <section className="py-24 bg-white" data-testid="content-editorial">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      {sections.map((s, i) => (
        <React.Fragment key={s.h2}>
          <div className="grid lg:grid-cols-[1fr_1.7fr] gap-6 lg:gap-16 py-12 border-b border-gray-100 last:border-0">
            <div className="lg:sticky lg:top-28 self-start">
              <span className="text-sm font-bold tracking-[0.2em]" style={{ color: C.accent }}>{String(i + 1).padStart(2, '0')} —</span>
              <h2 className="text-3xl font-bold text-gray-900 mt-3 leading-tight" style={serif}>{s.h2}</h2>
            </div>
            <div>{s.p.map(t => <p key={t} className="text-gray-600 text-[15px] leading-[1.85] mb-4">{t}</p>)}</div>
          </div>
          {i === 1 && (
            <div className="relative my-8 rounded-3xl overflow-hidden h-72 sm:h-96">
              <img src={image} alt={imageAlt} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${C.bg}f0 0%, ${C.bg}99 50%, transparent 100%)` }} />
              <div className="relative h-full flex flex-col justify-center p-8 sm:p-14 max-w-xl">
                <Quote className="w-10 h-10 mb-4" style={{ color: C.accent }} />
                <p className="text-white text-xl sm:text-2xl leading-snug mb-4" style={serif}>"{d.testimonials[2].review}"</p>
                <span className="text-sm text-gray-400">{d.testimonials[2].name} · Google Review</span>
              </div>
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  </section>
);

const Reviews = () => (
  <section className="py-24" style={{ background: C.card }} data-testid="reviews-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-14">
        <h2 className="text-4xl lg:text-5xl font-bold text-white mb-3" style={serif}>What Our Clients Say</h2>
        <div className="flex items-center justify-center gap-2">
          <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />)}</div>
          <span className="text-gray-300 font-semibold">5.0 on Google</span>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-5">
        {[d.testimonials[0], d.testimonials[4], d.testimonials[5]].map((t, i) => (
          <div key={t.name} className="rounded-2xl p-7 hover:-translate-y-1 transition-transform duration-300" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }} data-testid={`review-card-${i}`}>
            <div className="flex mb-4">{[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
            <p className="text-gray-300 text-sm leading-relaxed mb-6">"{t.review}"</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ background: `linear-gradient(135deg, ${C.accent}, ${C.accentLight})` }}>{t.name.charAt(0)}</div>
              <div><p className="text-white font-semibold text-sm">{t.name}</p><p className="text-xs text-gray-500">{t.time}</p></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Faqs = ({ faqs }) => {
  const [open, setOpen] = useState(0);
  return (
    <section className="py-24" style={{ background: C.bg }} data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <Eyebrow>Got Questions?</Eyebrow>
          <h2 className="text-4xl font-bold text-white" style={serif}>Frequently Asked Questions</h2>
        </div>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <div key={f.q} className="rounded-xl overflow-hidden transition-colors" style={{ background: open === i ? `${C.accent}0d` : 'rgba(255,255,255,0.02)', border: `1px solid ${open === i ? `${C.accent}40` : 'rgba(255,255,255,0.06)'}` }}>
              <button className="w-full flex items-center justify-between px-6 py-5 text-left" onClick={() => setOpen(open === i ? null : i)} data-testid={`faq-toggle-${i}`}>
                <span className="text-white font-semibold text-sm sm:text-base pr-4">{f.q}</span>
                <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${open === i ? 'rotate-180' : ''}`} style={{ color: C.accent }} />
              </button>
              {open === i && <p className="text-gray-400 text-sm leading-relaxed px-6 pb-5" data-testid={`faq-answer-${i}`}>{f.a}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Services = ({ onBook }) => (
  <section className="py-24 bg-[#f7f7f9]" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4" style={serif}>Our Services</h2>
        <p className="text-gray-500 max-w-lg mx-auto">Premium entertainment for every occasion</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {d.services.map((s, i) => (
          <div key={s.title} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300 border border-gray-100" data-testid={`service-card-${i}`}>
            <div className="relative h-60 overflow-hidden">
              <img src={s.image.replace('./', '/')} alt={s.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <h3 className="absolute bottom-4 left-5 text-xl font-bold text-white" style={serif}>{s.title}</h3>
            </div>
            <div className="p-5">
              <p className="text-gray-600 text-sm mb-4 leading-relaxed">{s.description}</p>
              <ul className="space-y-2 mb-5">
                {s.features.map(f => <li key={f} className="flex items-center text-sm text-gray-700"><CheckCircle2 className="w-4 h-4 mr-2 flex-shrink-0" style={{ color: C.accent }} />{f}</li>)}
              </ul>
              <Button variant="outline" className="w-full text-sm font-semibold bg-transparent hover:!bg-[#FD3DB5] hover:!text-white hover:!border-[#FD3DB5] transition-colors" style={{ borderColor: `${C.accent}60`, color: C.accent }} onClick={onBook} data-testid={`service-enquire-${i}`}>Enquire Now</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Cta = ({ cta, onBook }) => (
  <section className="relative py-24 overflow-hidden" style={{ background: `linear-gradient(135deg, #2a0a1f 0%, ${C.bg} 55%, #1d0716 100%)` }} data-testid="cta-section">
    <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full blur-[120px] opacity-40" style={{ background: C.accent }} />
    <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full blur-[120px] opacity-25" style={{ background: C.accent }} />
    <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="text-4xl lg:text-5xl font-bold text-white mb-5" style={serif}>{cta.title}</h2>
      <p className="text-gray-300 mb-10 text-lg">{cta.text}</p>
      <div className="flex flex-col sm:flex-row gap-5 justify-center items-center">
        <BookButton onClick={onBook} label="Book Your Show" testId="cta-enquire-btn" />
        <a href="tel:0428909900" className="text-white font-semibold hover:text-[#FD3DB5] transition-colors" data-testid="cta-phone-link">or call 0428 909 900</a>
      </div>
    </div>
  </section>
);

export const makeServicePage = ({ seo, hero, highlights, intro: introMeta, sections, models, faqs, cta }) => function ServicePage() {
  const intro = { ...introMeta, p: sections[0].p };
  const cardSections = sections.slice(1, 5);
  const editorial = sections.slice(5);
  return (
    <PageLayout pageTitle={seo.title} metaDescription={seo.description} path={seo.path}>
      {({ setIsModalOpen }) => {
        const book = () => setIsModalOpen(true);
        return (
          <>
            <Hero hero={hero} onBook={book} />
            <Highlights items={highlights} />
            <IntroSplit intro={intro} image={hero.image} imageAlt={hero.h1 + ' ' + hero.h1Accent} onBook={book} />
            {cardSections.length > 0 && <CardSections sections={cardSections} />}
            <ModelsModule subtitle={models.subtitle} offset={models.offset} onBook={book} />
            <EditorialSections sections={editorial} image={hero.bannerImage} imageAlt={hero.badge} />
            <Reviews />
            <Faqs faqs={faqs} />
            <Services onBook={book} />
            <Cta cta={cta} onBook={book} />
          </>
        );
      }}
    </PageLayout>
  );
};
